<template>
  <AuthFrame :title="t('auth.resetTitle')" :lead="t('auth.resetLead')">
    <form v-if="token" class="panel" @submit.prevent="submit">
      <label class="field">
        <span>{{ t('auth.newPassword') }}</span>
        <PasswordInput
          v-model="password"
          autocomplete="new-password"
          :minlength="passwordMinLen"
          :placeholder="t('auth.passwordHint')"
          required
        />
      </label>
      <label class="field">
        <span>{{ t('auth.confirmPassword') }}</span>
        <PasswordInput
          v-model="confirm"
          autocomplete="new-password"
          :minlength="passwordMinLen"
          required
        />
      </label>
      <p v-if="errorKey" class="error">{{ t(errorKey) }}</p>
      <button type="submit" class="cta" :disabled="auth.busy">
        {{ t('auth.resetSave') }}
      </button>
    </form>
    <p v-else class="panel sent">{{ t('auth.resetMissing') }}</p>
    <template #footer>
      <p class="switch">
        <router-link :to="{ name: 'forgot-password' }">{{ t('auth.forgotLink') }}</router-link>
      </p>
    </template>
  </AuthFrame>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AuthFrame from '../components/AuthFrame.vue'
import PasswordInput from '../components/PasswordInput.vue'
import { useI18n } from '../i18n/t'
import { ApiError } from '../api/http'
import { PASSWORD_MIN_LEN, useAuthStore } from '../stores/auth'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const password = ref('')
const confirm = ref('')
const errorKey = ref('')
const passwordMinLen = PASSWORD_MIN_LEN

const token = computed(() => {
  const raw = route.query.token
  return typeof raw === 'string' ? raw : ''
})

function errorToKey(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'reset_invalid') return 'auth.errors.reset_invalid'
    if (err.code === 'validation') return 'auth.errors.validation'
    if (err.code === 'rate_limited') return 'auth.errors.rate_limited'
    if (
      err.code === 'database_unavailable' ||
      err.code === 'reset_failed' ||
      err.status === 503
    ) {
      return 'auth.errors.database_unavailable'
    }
  }
  return 'auth.errors.network'
}

async function submit(): Promise<void> {
  errorKey.value = ''
  if (password.value !== confirm.value) {
    errorKey.value = 'auth.errors.password_mismatch'
    return
  }
  try {
    await auth.resetPassword({ token: token.value, password: password.value })
    await router.replace('/')
  } catch (err) {
    errorKey.value = errorToKey(err)
  }
}
</script>

<style scoped>
.sent {
  margin: 0;
  color: var(--md-sys-color-on-surface);
  font-size: 14px;
  line-height: 20px;
}
</style>
